import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PlateAnalyzer } from "@/components/PlateAnalyzer";

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];

const localInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const blank = () => ({
  name: "",
  meal_type: "lunch",
  calories: "",
  protein: "",
  carbs: "",
  fat: "",
  portion_after: "0",
  notes: "",
  consumed_at: localInput(),
});

export const MealDialog = ({ open, onOpenChange, meal, onSubmit }) => {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState({
    beforeFile: null,
    afterFile: null,
    photo_url: null,
    after_photo_url: null,
  });

  useEffect(() => {
    if (!open) return;
    setPhotos({
      beforeFile: null,
      afterFile: null,
      photo_url: meal?.photo_url || null,
      after_photo_url: meal?.after_photo_url || null,
    });
    setForm(
      meal
        ? {
            name: meal.name,
            meal_type: meal.meal_type,
            calories: String(meal.calories ?? ""),
            protein: String(meal.protein ?? ""),
            carbs: String(meal.carbs ?? ""),
            fat: String(meal.fat ?? ""),
            portion_after: String(meal.portion_after ?? 0),
            notes: meal.notes || "",
            consumed_at: localInput(meal.consumed_at),
          }
        : blank()
    );
  }, [open, meal]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        meal_type: form.meal_type,
        calories: Number(form.calories) || 0,
        protein: Number(form.protein) || 0,
        carbs: Number(form.carbs) || 0,
        fat: Number(form.fat) || 0,
        portion_before: 100,
        portion_after: Math.max(0, Math.min(100, Number(form.portion_after) || 0)),
        photo_url: photos.photo_url || null,
        after_photo_url: photos.after_photo_url || null,
        notes: form.notes.trim() || null,
        consumed_at: `${form.consumed_at}:00`.slice(0, 19),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-sand border-[#E8E3D9] sm:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="meal-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-deep-olive tracking-tight">
            {meal ? "Edit meal" : "Log a meal"}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-grey">
            Snap the plate before and after, or type the numbers in yourself.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <PlateAnalyzer
            photos={photos}
            setPhotos={setPhotos}
            onResult={(r) =>
              setForm((f) => ({
                ...f,
                name: r.name,
                meal_type: r.meal_type,
                calories: String(r.calories),
                protein: String(r.protein),
                carbs: String(r.carbs),
                fat: String(r.fat),
                portion_after: String(r.portion_after),
              }))
            }
          />


          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">Meal</Label>
            <Input
              data-testid="meal-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Nasi, ayam bakar & sayur"
              className="mt-1.5 bg-white border-[#E8E3D9]"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">Type</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {MEAL_TYPES.map((t) => (
                <Button
                  key={t}
                  type="button"
                  data-testid={`meal-type-${t}`}
                  onClick={() => setForm((f) => ({ ...f, meal_type: t }))}
                  className={`h-9 rounded-full px-4 text-sm capitalize transition-colors duration-200 ${
                    form.meal_type === t
                      ? "bg-forest text-sand hover:bg-forest-hover"
                      : "bg-surface text-stone-grey hover:bg-surface-hover hover:text-deep-olive"
                  }`}
                >
                  {t}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              ["calories", "Calories (kcal)", "meal-calories-input"],
              ["protein", "Protein (g)", "meal-protein-input"],
              ["carbs", "Carbs (g)", "meal-carbs-input"],
              ["fat", "Fat (g)", "meal-fat-input"],
            ].map(([k, label, tid]) => (
              <div key={k}>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">
                  {label}
                </Label>
                <Input
                  data-testid={tid}
                  type="number"
                  min="0"
                  step="0.1"
                  value={form[k]}
                  onChange={set(k)}
                  className="mt-1.5 bg-white border-[#E8E3D9]"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">
                Leftover %
              </Label>
              <Input
                data-testid="meal-leftover-input"
                type="number"
                min="0"
                max="100"
                value={form.portion_after}
                onChange={set("portion_after")}
                className="mt-1.5 bg-white border-[#E8E3D9]"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">
                When
              </Label>
              <Input
                data-testid="meal-datetime-input"
                type="datetime-local"
                required
                value={form.consumed_at}
                onChange={set("consumed_at")}
                className="mt-1.5 bg-white border-[#E8E3D9]"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-moss">Notes</Label>
            <Textarea
              data-testid="meal-notes-input"
              value={form.notes}
              onChange={set("notes")}
              rows={2}
              className="mt-1.5 bg-white border-[#E8E3D9]"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              data-testid="meal-cancel-button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-10 rounded-full px-5 text-stone-grey hover:bg-surface-hover hover:text-deep-olive transition-colors duration-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="meal-save-button"
              disabled={saving}
              className="h-10 rounded-full bg-forest px-6 text-sand hover:bg-forest-hover transition-colors duration-200"
            >
              {saving ? "Saving…" : meal ? "Save changes" : "Add meal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
